/**
 * mm-cart-intercept.js — window.mmCartAdd
 *
 * The add engine behind the drawer's own buttons (upsell, set promo, reward
 * gifts). An add is a *single* round trip: the add request asks for the drawer
 * section back, we paint from that response, then we open the drawer.
 *
 * It does NOT intercept the theme's add surfaces — see the "interception"
 * section at the foot of this file for why. Based on
 * docs/MM-CART-DRAWER-SPEC.md §5.3.
 */
(function () {
  // Bound once so a later monkey-patch of window.fetch by an app script cannot
  // make the engine's own requests recurse through it.
  const originalFetch = window.fetch.bind(window);

  const routes = (window.theme && theme.routes) || {};
  const addUrl = `${routes.cart_add_url || '/cart/add'}.js`;
  const cartUrl = `${routes.cart_url || '/cart'}.js`;
  const updateUrl = `${routes.cart_update_url || '/cart/update'}.js`;

  const jsonHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const queue = [];
  let draining = false;
  let drainScheduled = false;
  let seq = 0;
  let renderedSeq = 0;
  let cartPromise = null;
  let settledResolvers = [];

  const nextSeq = () => ++seq;

  function sectionId() {
    return document.querySelector('#CartDrawer')?.dataset.sectionId || 'mm-cart-drawer';
  }

  function drawerElement() {
    return document.querySelector('mm-cart-drawer');
  }

  /* ------------------------------------------------------------- rendering */

  function applySections(sections, payloadSeq) {
    if (!sections) return;
    if (payloadSeq <= renderedSeq) return; // a slow response must never paint over a newer cart
    renderedSeq = payloadSeq;

    const items = document.querySelector('#CartDrawer mm-cart-items');
    if (items?.renderSections) {
      items.renderSections(sections);
    }

    readItemCountFromDrawer();
  }

  function readItemCountFromDrawer() {
    const node = document.querySelector('#CartDrawer .drawer__heading-amount');
    if (!node) return;

    const count = parseInt(node.textContent.trim(), 10);
    if (Number.isNaN(count)) return;

    const icon = document.querySelector('cart-icon');
    if (icon?.renderCartBubble) {
      icon.renderCartBubble(count, false);
    } else {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          detail: { data: { itemCount: count, source: 'mm-cart-drawer' } },
        })
      );
    }
  }

  function applyCart(cart) {
    if (!cart) return;
    mmCartAdd.cart = cart;

    const items = document.querySelector('#CartDrawer mm-cart-items');
    items?.schedulePostCartUpdateWork?.(true, cart);
  }

  /* ---------------------------------------------------------------- queue */

  /** Collapses repeat adds of the same [id, selling_plan, properties] signature. */
  function mergeItems(items) {
    const merged = new Map();

    items.forEach((item) => {
      const signature = JSON.stringify([item.id, item.selling_plan || '', item.properties || null]);
      const existing = merged.get(signature);

      if (existing) {
        existing.quantity += item.quantity || 1;
      } else {
        merged.set(signature, { ...item, quantity: item.quantity || 1 });
      }
    });

    return Array.from(merged.values());
  }

  function enqueue(entry) {
    queue.push(entry);
    scheduleDrain();
    return entry.promise;
  }

  function scheduleDrain() {
    if (drainScheduled || draining) return;
    drainScheduled = true;

    // One macrotask hop so everything queued in the same tick batches together.
    setTimeout(() => {
      drainScheduled = false;
      drain();
    }, 0);
  }

  /** A rawBody entry always travels alone; everything else batches. */
  function takeBatch() {
    if (queue.length === 0) return null;
    if (queue[0].rawBody) return [queue.shift()];

    const batch = [];
    while (queue.length > 0 && !queue[0].rawBody) batch.push(queue.shift());
    return batch;
  }

  async function drain() {
    if (draining) return;
    draining = true;

    try {
      let batch;
      while ((batch = takeBatch())) {
        // Shopify locks the cart per session, so parallel adds are slower than
        // sequential ones and can shuffle line order.
        // eslint-disable-next-line no-await-in-loop
        await processBatch(batch);
      }
    } finally {
      draining = false;

      if (queue.length > 0) {
        scheduleDrain();
      } else {
        const resolvers = settledResolvers;
        settledResolvers = [];
        resolvers.forEach((resolve) => resolve());
      }
    }
  }

  async function processBatch(batch) {
    const wantsDrawer = batch.some((entry) => entry.openDrawer !== false);
    const discountCodes = batch.flatMap((entry) => (entry.discountCode ? [entry.discountCode] : []));

    const drawer = drawerElement();
    const wasOpen = drawer?.classList.contains('active');
    if (wasOpen) drawer.classList.add('is-busy');

    const payloadSeq = nextSeq();

    try {
      let response;

      if (batch[0].rawBody) {
        const body = batch[0].rawBody.body;
        if (body instanceof FormData) body.set('sections', sectionId());

        response = await originalFetch(batch[0].rawBody.action || addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
      } else {
        const items = mergeItems(batch.flatMap((entry) => entry.items));
        response = await originalFetch(addUrl, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ items, sections: sectionId() }),
        });
      }

      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || 'Could not add to cart.';
        const errorTarget = document.querySelector('#CartDrawer-CartErrors');
        if (errorTarget) errorTarget.textContent = message;

        batch.forEach((entry) => entry.resolve({ ok: false, error: message, data }));
        return;
      }

      // Paint from the add response, then open — never optimistically.
      applySections(data.sections, payloadSeq);

      if (wantsDrawer && drawer) {
        if (wasOpen) {
          drawer.classList.remove('is-busy');
        } else {
          drawer.show();
        }
      }

      batch.forEach((entry) => entry.resolve({ ok: true, data }));

      // Off the critical path.
      afterAdd(discountCodes);
    } catch (error) {
      console.error('[mm-cart] add failed', error);
      batch.forEach((entry) => entry.resolve({ ok: false, error }));
    } finally {
      if (wasOpen) drawer?.classList.remove('is-busy');
    }
  }

  async function afterAdd(codes) {
    try {
      const cart = await mmCartAdd.getCart(true);
      if (codes.length > 0) await applyDiscountCodes(cart, codes);
      applyCart(mmCartAdd.cart || cart);
    } catch (error) {
      console.error('[mm-cart] post-add work failed', error);
    }
  }

  async function applyDiscountCodes(cart, codes) {
    const existing = (cart?.discount_codes || []).map((entry) => entry.code);
    const merged = Array.from(
      new Map([...existing, ...codes].filter(Boolean).map((code) => [code.toLowerCase(), code])).values()
    );

    await originalFetch(updateUrl, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ discount: merged.join(',') }),
    }).catch(() => {});

    // Also hit /discount/{code} so the code survives to checkout.
    codes.forEach((code) => {
      originalFetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' }).catch(() => {});
    });

    mmCartAdd.cart = await mmCartAdd.getCart(true);
  }

  /* -------------------------------------------------------------- public */

  const mmCartAdd = {
    cart: null,

    add(items, options = {}) {
      const { discountCode = '', openDrawer = true, rawBody = null } = options;

      let resolve;
      const promise = new Promise((r) => {
        resolve = r;
      });

      return enqueue({
        items: Array.isArray(items) ? items : [items],
        discountCode,
        openDrawer,
        rawBody,
        promise,
        resolve,
      });
    },

    settled() {
      if (!draining && queue.length === 0) return Promise.resolve();

      return new Promise((resolve) => {
        settledResolvers.push(resolve);
        setTimeout(resolve, 10000); // safety valve
      });
    },

    openDrawer() {
      drawerElement()?.show();
    },

    getCart(force = false) {
      if (!force && cartPromise) return cartPromise;

      cartPromise = originalFetch(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          mmCartAdd.cart = cart;
          return cart;
        })
        .catch(() => null)
        .finally(() => {
          setTimeout(() => {
            cartPromise = null;
          }, 0);
        });

      return cartPromise;
    },
  };

  window.mmCartAdd = mmCartAdd;

  /* --------------------------------------------------------- interception */

  // Deliberately none.
  //
  // The reference build intercepted every `<form action="/cart/add">` submit and
  // patched window.fetch, because in that theme nothing else handled adds. This
  // theme is not that theme: Horizon's own components (product form, quick add,
  // mmx-product-information) each run their own add request and announce it with
  // the standard `shopify:cart:lines-update` event.
  //
  // Intercepting the submit on top of that added the line twice — the theme's
  // click handler posted its own add, and the form still submitted into this
  // engine, which merged both into one line of quantity 2.
  //
  // So the drawer listens for `shopify:cart:lines-update` instead (see
  // onThemeCartLinesUpdate in mm-cart.js), which is the same signal the stock
  // cart drawer auto-opened from. `mmCartAdd.add()` stays as the programmatic
  // API for the drawer's own buttons — upsell, set promo and reward gifts —
  // where the single-request add-and-render is still what we want.
})();
